import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Fetcher,
  useFetcher,
  useFetchers,
  useRouteLoaderData,
} from "react-router-dom";
import DeweyIntlProvider from "../../common/core-ui/DeweyIntlProvider.tsx";
import {
  HomeContextShape,
  HomeContext,
  homeRouteId,
  loadHomeData,
  ExploreDataShape,
  HomeRouteLoaderShape,
  loadExploreUser,
} from "./homeContext.ts";
import doLogin from "../root/helpers/loginHandler.ts";
import { getSystemErrorURL } from "../bookshelfLinks.ts";
import { useIntl } from "react-intl";
import { UserPreferenceKey } from "../../common/api/decimal/methods/user.ts";
import { noticeError } from "../../common/api/monitoring/index.ts";
import { useExpiredNotify } from "./common/expired/useExpiredNotify.ts";

type FetcherState = ReturnType<typeof useFetcher>["state"];

enum HomeStateActionType {
  Reload = "reload",
  PrefsSubmit = "submit",
}

type HomeStateAction =
  | { type: HomeStateActionType.Reload; data: HomeRouteLoaderShape }
  | { type: HomeStateActionType.PrefsSubmit; fetcher: Fetcher };

function reducer(state: HomeRouteLoaderShape, action: HomeStateAction) {
  // this is mainly a hook for optimistic UI
  // when a mutation occurs the user context data is reloaded, if something
  // asks for state in the space between the end of the mutation and the
  // reload of home data there could be a discrepancy, this is a chance to be optimistic
  // and presume a mutation succeeds, if it doesn't the state will flip back automatically

  switch (action.type) {
    case HomeStateActionType.Reload:
      // data refreshed after mutation
      return action.data;
    case HomeStateActionType.PrefsSubmit: {
      // make pref change optimistic
      const { formAction, formMethod, json } = action.fetcher;
      const slot = formAction?.split("/").pop();
      if (slot) {
        const newState = { ...state };
        if (formMethod === "DELETE") {
          // a delete wipes out the whole preference object
          newState.user.preferences[slot as UserPreferenceKey] = {};
        } else {
          // we can't be optimistic about the modifiedAt tick of the outer wrapper
          // but the pref payload is what matters and we can change that
          if (!newState.user.preferences[slot as UserPreferenceKey]) {
            // on first hit we won't have the slot
            newState.user.preferences[slot as UserPreferenceKey] = {};
          }
          newState.user.preferences[slot as UserPreferenceKey].value =
            json as object;
        }
        return newState;
      }
      return state;
    }
    default:
      return state;
  }
}

interface StatusHaver {
  status: number;
}
function hasStatus(item: unknown): item is StatusHaver {
  return !!(item as StatusHaver).status;
}

export const HomeContextProvider = ({ children }: { children: ReactNode }) => {
  // Do not export the loader data, as children should not use it
  const routeLoaderData = useRouteLoaderData(
    homeRouteId
  ) as HomeRouteLoaderShape;
  const intl = useIntl();
  const [currentLoaderData, dispatch] = useReducer(reducer, routeLoaderData);
  const [exploreData, setExploreData] = useState<ExploreDataShape>({
    hasExplore: false,
    creditList: [],
    hasAdmin: false,
    hasGroups: false,
    promotions: [],
    exploreBranding: {},
  });
  const [isExploreReady, setIsExploreReady] = useState(false);
  const statusRef = useRef<Record<string, FetcherState>>({});
  const fetchers = useFetchers();
  const mutatingFetchers = fetchers.filter((fetcher) =>
    ["PUT", "POST", "DELETE", "PATCH"].includes(fetcher.formMethod ?? "")
  );

  const { unseenExpiredCount } = useExpiredNotify(
    currentLoaderData.user.preferences[UserPreferenceKey.LastSeenExpiredAt]
      ?.value as string | undefined
  );

  const updateExploreUser = useCallback(() => {
    loadExploreUser()
      .then((exploreUser) => {
        setExploreData(exploreUser);
        setIsExploreReady(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    updateExploreUser();
  }, [updateExploreUser]);

  useEffect(() => {
    const status = statusRef.current;
    let needReload = false;
    for (const key in status) {
      // Fetchers transition from submitting -> loading to indicate a completed submission.
      // However, components without loaders don't have access to the submitting -> loading
      // state change. Sometimes the fetcher will transition directly to idle or will
      // completely disappear. These components may still need to watch for fetcher changes
      // (e.g. when a user changes language) so the following checks will allow this.
      const relatedFetcher = fetchers.find((fetcher) => fetcher.key === key);
      const submissionIsDone =
        status[key] === "submitting" &&
        (!relatedFetcher || relatedFetcher.state !== "submitting");

      if (submissionIsDone) {
        needReload = true;
        delete status[key];
      }
    }

    mutatingFetchers.forEach((fetcher) => {
      if (
        fetcher.state === "submitting" &&
        status[fetcher.key] !== "submitting"
      ) {
        if (fetcher.formAction?.indexOf("actions/user/preferences/") !== -1) {
          dispatch({ type: HomeStateActionType.PrefsSubmit, fetcher });
        }
      }
      status[fetcher.key] = fetcher.state;
    });

    if (needReload) {
      updateExploreUser();

      loadHomeData()
        .then((result) =>
          dispatch({
            type: HomeStateActionType.Reload,
            data: result.data as unknown as HomeRouteLoaderShape,
          })
        )
        .catch((error: unknown) => {
          if (hasStatus(error) && error.status === 401) {
            doLogin();
          } else {
            if (error instanceof Error) {
              noticeError(error);
            }
            window.location.assign(
              getSystemErrorURL({
                locale: currentLoaderData?.messageInfo?.locale ?? intl.locale,
              })
            );
          }
        });
    }
  }, [
    currentLoaderData?.messageInfo?.locale,
    fetchers,
    intl.locale,
    mutatingFetchers,
    updateExploreUser,
  ]);

  const content = currentLoaderData.messageInfo ? (
    <DeweyIntlProvider
      locale={currentLoaderData?.messageInfo.locale}
      messages={currentLoaderData?.messageInfo.messages}
    >
      {children}
    </DeweyIntlProvider>
  ) : (
    children
  );

  const providerData = useMemo<HomeContextShape>(() => {
    return {
      messageInfo: currentLoaderData.messageInfo,
      user: {
        ...currentLoaderData.user,
        ...exploreData,
        unseenExpiredCount,
      },
      isExploreReady,
      coursesExtras: currentLoaderData.coursesExtras,
      finAid: currentLoaderData.finAid,
      cartSummary: currentLoaderData.cartSummary,
    };
  }, [currentLoaderData, exploreData, isExploreReady, unseenExpiredCount]);

  return (
    <HomeContext.Provider value={providerData}>{content}</HomeContext.Provider>
  );
};
